import { useRef, useEffect, useState } from 'react';

// Adapted from the react-bits GooeyNav source you provided. The original
// pairs this logic with a GooeyNav.css file (not included in what you
// pasted) that supplies layout + a `.particle` keyframe animation. Instead
// of an external stylesheet, the nav/list/link layout is inline `style`
// below, and each particle animates via a plain CSS *transition* (set,
// then updated a frame later) rather than a `@keyframes` rule — same
// bubble-pop effect, no separate CSS file.
const GooeyNav = ({
  items,
  activeIndex = 0,
  onSelect,
  accentColor = '#7fd8d0',
  textColor = 'rgba(238,236,227,0.72)',
  activeTextColor = '#0a1210',
  particleCount = 12,
  particleDistances = [70, 10],
  particleR = 90,
  animationTime = 500,
}) => {
  const containerRef = useRef(null);
  const navRef = useRef(null);
  const filterRef = useRef(null);
  const [internalActive, setInternalActive] = useState(activeIndex);

  const noise = (n = 1) => n / 2 - Math.random() * n;

  const getXY = (distance, pointIndex, totalPoints) => {
    const angle = ((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180);
    return [distance * Math.cos(angle), distance * Math.sin(angle)];
  };

  const spawnParticles = (originEl) => {
    const filterEl = filterRef.current;
    if (!filterEl) return;
    const originRect = originEl.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const originX = originRect.left - containerRect.left + originRect.width / 2;
    const originY = originRect.top - containerRect.top + originRect.height / 2;

    for (let i = 0; i < particleCount; i++) {
      const [sx, sy] = getXY(particleDistances[0], particleCount - i, particleCount);
      const [ex, ey] = getXY(particleDistances[1] + noise(7), particleCount - i, particleCount);
      const time = animationTime * 2 + noise(200);
      const scale = 1 + noise(0.2);

      const particle = document.createElement('span');
      particle.style.position = 'absolute';
      particle.style.left = `${originX}px`;
      particle.style.top = `${originY}px`;
      particle.style.width = '5px';
      particle.style.height = '5px';
      particle.style.borderRadius = '50%';
      particle.style.background = accentColor;
      particle.style.pointerEvents = 'none';
      particle.style.transform = `translate(${sx}px, ${sy}px) scale(${scale})`;
      particle.style.opacity = '1';
      particle.style.transition = `transform ${time}ms ease, opacity ${time}ms ease`;
      filterEl.appendChild(particle);

      requestAnimationFrame(() => {
        particle.style.transform = `translate(${ex}px, ${ey}px) scale(0)`;
        particle.style.opacity = '0';
      });

      setTimeout(() => {
        particle.remove();
      }, time + 50);
    }
  };

  const handleClick = (e, index) => {
    if (internalActive === index) return;
    setInternalActive(index);
    spawnParticles(e.currentTarget);
    onSelect?.(items[index], index);
  };

  useEffect(() => {
    setInternalActive(activeIndex);
  }, [activeIndex]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <nav>
        <ul
          ref={navRef}
          style={{
            display: 'flex',
            gap: 4,
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {items.map((item, index) => {
            const isActive = internalActive === index;
            return (
              <li key={item.label}>
                <a
                  href={item.href}
                  onClick={(e) => handleClick(e, index)}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    padding: '9px 18px',
                    borderRadius: 999,
                    textDecoration: 'none',
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '0.72rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: isActive ? activeTextColor : textColor,
                    background: isActive ? accentColor : 'transparent',
                    transition: 'color .3s ease, background .3s ease',
                  }}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
      {/* particle burst layer */}
      <div ref={filterRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }} />
    </div>
  );
};

export default GooeyNav;
